import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * A "slide to confirm" security slider. Requires the user to drag the handle
 * all the way across the track before firing onConfirm, preventing a
 * destructive action from being triggered by an accidental click/tap.
 */
function SlideToConfirm({ label = 'Slide to confirm', onConfirm, disabled = false }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [percent, setPercent] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  const updateFromClientX = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const handleSize = rect.height;
    const travel = Math.max(rect.width - handleSize, 1);
    const raw = clientX - rect.left - handleSize / 2;
    const clamped = Math.min(Math.max(raw, 0), travel);
    setPercent((clamped / travel) * 100);
  }, []);

  const finishDrag = useCallback(() => {
    setDragging((wasDragging) => {
      if (!wasDragging) return false;
      setPercent((current) => {
        if (current >= 92) {
          setConfirmed(true);
          onConfirm && onConfirm();
          return 100;
        }
        return 0;
      });
      return false;
    });
  }, [onConfirm]);

  // Track the drag at the document level so fast mouse movement past the
  // slider's own bounds doesn't prematurely cancel the gesture.
  useEffect(() => {
    if (!dragging) return undefined;
    const handleMove = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      if (clientX !== undefined) updateFromClientX(clientX);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('mouseup', finishDrag);
    document.addEventListener('touchend', finishDrag);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('mouseup', finishDrag);
      document.removeEventListener('touchend', finishDrag);
    };
  }, [dragging, updateFromClientX, finishDrag]);

  const handlePointerDown = (e) => {
    if (disabled || confirmed) return;
    e.preventDefault();
    setDragging(true);
    updateFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };

  return (
    <div
      ref={trackRef}
      className={`slide-to-confirm-track${confirmed ? ' confirmed' : ''}${disabled ? ' disabled' : ''}`}
    >
      <div className="slide-to-confirm-fill" style={{ width: `calc((100% - 1.75rem) * ${percent / 100} + ${percent > 0 ? '1.75rem' : '0px'})` }} />
      <span className="slide-to-confirm-label">{confirmed ? 'Confirmed' : label}</span>
      <div
        className="slide-to-confirm-handle"
        style={{ left: `calc((100% - 1.75rem) * ${percent / 100})` }}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        role="slider"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        tabIndex={disabled ? -1 : 0}
      >
        &#8594;
      </div>
    </div>
  );
}

export default SlideToConfirm;

